import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React from "react";
import { Button } from "@/components/ui/button";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { Plus, Trash2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { asArray } from "@/lib/asArray";

/* Batch 23 · Stages 1 and 6(a) — the table section of a form and the section-width rule, kept in their own file
   (new code in a new component file — the big-file rule). Display only: the saved rows are unchanged. */

// Batch 23 · Stage 6(a): a section takes the full row (does not sit in a column) when it holds a table, a free-text box or
// many fields — those need the width; question lists and short field sets flow in columns.
export function sectionSpansRow(sec) {
  if (!sec) return false;
  if (sec.type === "table") return true;
  if (sec.type === "fields") {
    const fields = asArray(sec.fields);
    return fields.some((f) => f.type === "textarea" || f.type === "table") || fields.length > 6;
  }
  return false;
}

// Batch 23 · Stage 1: a sensible minimum width per column by what it holds (label words), so a description gets the room and
// an id / Yes-No / date / amount column stays narrow. The table grows to the window; it scrolls sideways only when the form
// has more columns than fit, and then the FIRST column stays in view (sticky, mirrored in RTL by the logical inset).
const COL_MIN = { short: 110, date: 130, num: 140, text: 260 };
export function columnKind(label) {
  const l = String(label || "").toLowerCase();
  if (/\bdate\b|تاريخ/.test(l)) return "date";
  if (/amount|balance|value|\bsar\b|%|ratio|rate|variance|total|threshold|materiality|\bfee|cost|number|\bno\.?$|count|qty|quantity|المبلغ|الرصيد|النسبة/.test(l)) return "num";
  if (/^ref|\bref\b|\bid\b|^#|\bcode\b|y\/n|yes\s*\/\s*no|status|level|rating|likelihood|\bimpact\b|assertion|\bw\/p\b|المرجع|الحالة/.test(l)) return "short";
  return "text";
}

export function TableSection({ section, rows, onChange, readOnly = false, manualLabel = "" }) {
  const { t } = useI18n();
  const cols = section.columns || [];
  // Column entries in forms_library.json come in TWO historical shapes:
  //   1. plain strings ("Description", "Amount", …)
  //   2. objects { id, label }  — used by 10 audit forms (108(a), 211(a),
  //      214(a), 215(a), 313(a), 315(a), 316(a), 317(a), 322(a), 400(a))
  // Rendering an object directly inside JSX throws React error #31
  // ("Objects are not valid as a React child"). Coerce every entry to a
  // display string before it hits the header row.
  const colLabel = (c) => (typeof c === "string" ? c : (c?.label ?? c?.id ?? ""));
  const setCell = (rIdx, cIdx, v) => {
    const next = rows.map((r, i) => i === rIdx ? { ...r, [cIdx]: v } : r);
    onChange(next);
  };
  const addRow = () => onChange([...rows, {}]);
  // Step 11d · add-on: adjustable columns — the default width of a column is its kind's minimum (COL_MIN), the delete column 48 px
  const formW = [...cols.map((c) => COL_MIN[columnKind(colLabel(c))]), ...(section.editable !== false ? [48] : [])];
  const formCols = useResizableColumns({ storageKey: `form-table-${section.id}`, defaults: formW,
                                         mins: formW.map((w) => Math.min(w, 90)) });
  const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="overflow-x-auto" data-testid={`table-scroll-${section.id}`}>
        <ResizableTable cols={formCols} className="w-full border-collapse text-[12px]" data-testid={`table-${section.id}`}>
          <thead><tr className="border-b bg-slate-50 dark:bg-slate-800">
            {cols.map((c, i) => {
              const kind = columnKind(colLabel(c));
              return (
                <ResizableTh key={i} index={i} style={{ minWidth: COL_MIN[kind] }} data-col-kind={kind}
                    className={`px-2 py-1 text-start align-bottom font-medium ${i === 0 ? "sticky start-0 z-[1] bg-slate-50 dark:bg-slate-800" : ""}`}>{colLabel(c)}</ResizableTh>
              );
            })}
            {section.editable !== false && <ResizableTh index={cols.length} className="w-8"></ResizableTh>}
          </tr></thead>
          <tbody>
            {rows.map((r, rIdx) => (
              <tr key={rIdx} className="border-b align-top" data-testid={`row-${section.id}-${rIdx}`} data-manual={r._manual ? "true" : undefined}>
                {cols.map((_, cIdx) => (
                  <td key={cIdx} className={`p-1 align-top ${cIdx === 0 ? "sticky start-0 z-[1] bg-white dark:bg-slate-900" : ""}`}>
                    {readOnly ? (
                      <div className="min-h-7 whitespace-pre-wrap break-words px-1 py-0.5 text-[12px]" dir="auto" data-testid={`cell-${section.id}-${rIdx}-${cIdx}`}>
                        {r[cIdx] || ""}{cIdx === 0 && r._manual && manualLabel ? <span className="ms-1 rounded bg-amber-100 px-1 text-[9px] text-amber-900" data-testid={`row-manual-${section.id}-${rIdx}`}>{manualLabel}</span> : null}
                      </div>
                    ) : (
                      <AutoGrowTextarea value={r[cIdx] || ""} onChange={(e) => setCell(rIdx, cIdx, e.target.value)} dir="auto"
                             className="min-h-7 px-1.5 py-1 text-[12px] leading-4"
                             data-testid={`cell-${section.id}-${rIdx}-${cIdx}`} />
                    )}
                  </td>
                ))}
                {section.editable !== false && (
                  <td className="p-1 align-top"><Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => delRow(rIdx)}
                                                data-testid={`row-del-${section.id}-${rIdx}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button></td>
                )}
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </div>
      {section.editable !== false && (
        <Button size="sm" variant="outline" onClick={addRow} className="mt-2" data-testid={`row-add-${section.id}`}>
          <Plus className="me-1 h-3 w-3" /> {t("formRenderer.addRow")}
        </Button>
      )}
    </div>
  );
}
